import { action } from '@ember/object';
import { service } from '@ember/service';
import type { SafeString } from '@ember/template';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';

import type { ConnectWrapperObject } from '@databox/ember-integrations/lib/components/Connect/Connect';
import Connect, {
	type ConnectEntryPointProps,
} from '@databox/ember-integrations/lib/components/Connect/ConnectEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AiChatRouteSource } from 'webapp/enums/ai-chat';
import { AccountType, UserLevel } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { copyObject, getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

import { getBillingFeature } from 'webapp/models/helpers/react-model-mapper';

import type { ConnectedSourceData, ConnectSuccessMetadata } from 'webapp/services/connect';
import type MetricMoverService from 'webapp/services/metric-mover';

export default class SourceConnectContainerComponent extends Component {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('connect') declare connectService: Service.Connect;
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('metric-mover') declare metricMoverService: MetricMoverService;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;
	invalidatedGetBillingFeaturesQuery: (() => void) | null = null;

	get initialRequestsDone(): boolean {
		return this.navigationService.initialRequestsDone;
	}

	get isAtOnboardingSelectAccountTypeRoute(): boolean {
		return this.navigationService.activatedRoute.startsWith('onboarding.account-type');
	}

	get canRender(): boolean {
		return this.initialRequestsDone && !this.isAtOnboardingSelectAccountTypeRoute;
	}

	get defaultContainerStyle(): SafeString {
		return htmlSafe('position: relative; z-index: 999;');
	}

	@action
	onReactInit(args: ConnectWrapperObject) {
		this.connectService.connectFunctions = args;
	}

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderConnect();

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderConnect);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderConnect);
		this.eventManagerService.on(AppEvent.ONBOARDING_COMPLETED, this, this.renderConnect);
		this.eventManagerService.on(AppEvent.USER_VERIFIED, this, this.renderConnect);
	}

	renderConnect() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: ConnectEntryPointProps = {
			showHelpArticles: true,
			onConnectSuccess: this.handleConnectSuccess,
			onConnectFailed: this.handleConnectFailed,
			onConnectInterrupted: this.handleConnectFailed,
			onInviteUserSuccess: this.connectService.handleAfterInviteUserSuccessCallback,
			onInit: this.onReactInit,
			accessToken: accessToken!,
			dataToken: dataToken!,
			timezones: this.constantsService.timezones,
			isDarkThemeActive: this.navigationService.theme.isDark,
			spaceId: this.accountService.id,
			isWhiteLabeled: this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			companyInfo: {
				owner: this.accountService?.ownerId
					? {
							avatar: this.accountService.accountOwner!.avatar,
							email: this.accountService.accountOwner!.email,
							id: +this.accountService.accountOwner!.id!,
							name: this.accountService.accountOwner!.name,
						}
					: undefined,
				accountName: this.accountService.company,
			},
			replaceHelpUrls: this.accountService.replaceHelpUrl,
			userProfile: {
				rootLevel: this.accountService.isAdminButNotInClientSpace
					? UserLevel.ADMIN
					: UserLevel.USER,
				id: +this.currentUserService.id!,
			},
			onConnectInitializationFinished: () => {
				this.eventManagerService.trigger(AppEvent.CONNECT_INITIALIZATION_FINISHED);
			},
			isAccountTypeAgency: this.accountService.accountType === AccountType.AGENCY,
			isAccountTypeClient: this.accountService.accountType === AccountType.CLIENT,
			isUserEmailVerified: this.currentUserService.isVerified,
			isCurrentUserOnboarded: this.currentUserService.isOnboarded,
			onCreateMetrics: (sourceId: number) => {
				this.mmCustomQueriesService.openCreateNewPopup(
					'dataset_created_succesfully',
					undefined,
					sourceId,
				);
			},
			onViewDataset: (spaceAccessId: number) => {
				// A dataset set up while the Genie chat is on screen should leave the user in their
				// thread. The connect package tears the setup dialog down before calling this, so
				// bailing out here is all it takes to stay put.

				if (this.navigationService.activatedRoute.includes(AiChatRouteSource.GENIE)) {
					return;
				}

				this.navigationService.transitionToRoute(
					'datamanager.connected.datasets.view',
					spaceAccessId,
				);
			},
			onValidationError: (error: ReactIntegrations.Error) => {
				this.datasetEventsService.handleApiError(error);
			},
			onPlanUpgrade: (featureName: string) =>
				this.billingService.handleLimitationsForPlanUpgrade(
					getBillingFeature(featureName),
					'datasets',
				),
			onOpenConnectionsList: () => {
				this.navigationService.transitionToRoute('datamanager.available.index').then(() => {
					window.location.hash = 'all|dataset|Popularity';
				});
			},
			onApiError: (error: ReactIntegrations.Error) => {
				this.datasetEventsService.handleApiError(error);
			},
			onDatasetFinishSetupSuccess: this.datasetEventsService.handleDatasetFinishSetupSuccess,
			onWatchVideo: (videoUrl: string) => this.navigationService.openVideoPopup(videoUrl),
			legacyDisabled: this.metricMoverService.isLegacyDeprecationActive,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				Connect(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.ONBOARDING_COMPLETED, this);
		this.eventManagerService.off(AppEvent.USER_VERIFIED, this);
	}

	@action
	handleConnectSuccess(sourceData: ConnectedSourceData, metadata?: ConnectSuccessMetadata) {
		if (this.connectService.afterConnectPopupDoneCallback) {
			this.connectService.afterConnectPopupDoneCallback(
				// We are getting from react immutable state object, so we need to clone it
				// otherwise we would get an error when we try to modify it in the serializer
				copyObject(sourceData),
				undefined,
				metadata,
			);
		}
	}

	@action
	handleConnectFailed(error: string) {
		this.eventManagerService.trigger(AppEvent.CONNECT_INITIALIZATION_FINISHED);
		if (this.connectService.afterConnectPopupDoneCallback) {
			this.connectService.afterConnectPopupDoneCallback(undefined, error);
		}
	}
	<template>
		{{#if this.canRender}}
			<div
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
				style={{this.defaultContainerStyle}}
			>
			</div>
		{{/if}}
	</template>
}
